/**
 * The **Arena battlefield** (Arena minigame, PRD #334; endless since PRD #387
 * Slice C) - the unbounded open plain a solo horde-survival run is fought on.
 *
 * Every run constructs its own private, seeded {@link ../world.World} around this
 * map (the "run is a second World instance" decision). Since PRD #387 the field
 * is an **open-plain map** ({@link ./map.MapData.plain}): no walls, no edges, no
 * tile grid at all - walkability is `true` at every coordinate and one grass
 * tile repeats across the complete field. The whole
 * map is a ~hundred-byte sentinel descriptor on the wire (the run snapshot got
 * *smaller*), every run everywhere fights on the identical endless field, and
 * the horde - never the map - is the only thing that can kill the runner: the
 * spawn-ring/recycle pair (ADR-0203) is the anti-kiting mechanism the wall used
 * to be.
 *
 * Fully deterministic (no randomness at all, not even the seeded rng), so tests
 * can assert exact walkability and ground at any coordinate.
 */
import type { MapData } from './map';
import { tiles } from './content';
import type { SpawnPoint } from './authoredMap';

/**
 * Nominal edge length recorded on the map data (`width`/`height`). On an open
 * plain this is **informational only** - nothing bounds movement or spawns -
 * but the value keeps the runner's arrival tile centred where the old walled
 * field had it, so coordinates stay familiar in logs and tests.
 */
export const ARENA_MAP_SIZE = 80;

/**
 * The arena map plus the gameplay anchor the run choreography draws from. The
 * {@link ./arena.startArenaRun} entrypoint takes this whole object so the run's
 * spawn placement can never drift from the authored field.
 */
export interface ArenaMap {
  data: MapData;
  /** The runner's arrival tile (the old walled field's centre). */
  playerSpawn: SpawnPoint;
}

/**
 * The one grass tile repeated across the endless field.
 * Keeping the palette singular lets presentation use a constant-size repeated
 * plane instead of materializing a camera-sized set of logical tiles.
 */
const GRASS_PALETTE = [tiles.get('grass').id];

/**
 * Build the arena battlefield: the endless open plain. No walls, no statics,
 * no grid - just the sentinel descriptor ({@link ./map.MapData.plain}) carrying
 * the grass palette and the arrival tile. Fully deterministic - two calls
 * return identical data (asserted in tests), so a run world can be rebuilt
 * bit-for-bit from just its seed.
 */
export function makeArenaMap(): ArenaMap {
  const playerSpawn: SpawnPoint = { x: ARENA_MAP_SIZE / 2, y: ARENA_MAP_SIZE / 2 };
  const data: MapData = {
    width: ARENA_MAP_SIZE,
    height: ARENA_MAP_SIZE,
    palette: [...GRASS_PALETTE],
    cells: [],
    blocked: [],
    plain: true,
    playerSpawn,
  };
  return { data, playerSpawn };
}
